import { useState } from 'react'
import SEO from '../components/SEO'
import { trackContactSubmit } from '../utils/analytics'
import { SpeakerWaveIcon, UserGroupIcon } from '@heroicons/react/24/outline'

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('https://formspree.io/f/mkgkwyno', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setSubmitStatus('success')
        trackContactSubmit(formData.subject)
        setFormData({ name: '', email: '', subject: '', message: '' })
      } else {
        setSubmitStatus('error')
      }
    } catch {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <SEO 
        title="Contact"
        description="Connect with Kim Synergy for speaking engagements, book club discussions, media interviews, or general inquiries about Indoctri-NATION 365 and dismantling systemic racism."
        keywords="speaking engagement, diversity speaker, anti-racism workshop, book club discussion, Kim Synergy contact, racial justice speaker, educational workshop"
        url="https://indoctrination365.com/contact"
      />
      <main className="py-16">
        <div id="top" />
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold mb-6">Join the Conversation</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to connect, learn, or take action? I'm here to support your journey 
            toward understanding and meaningful change.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <h2 className="text-2xl font-semibold mb-6">Send a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="your.email@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                >
                  <option value="">Select a topic</option>
                  <option value="speaking">Speaking Engagement</option>
                  <option value="book-club">Book Club Discussion</option>
                  <option value="media">Media Interview</option>
                  <option value="general">General Question</option>
                  <option value="feedback">Book Feedback</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
                  placeholder="Tell me about your question, event, or how I can help..."
                />
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800">Thank you for your message! I'll get back to you soon.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800">Sorry, there was an error sending your message. Please try again.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-600 text-white px-6 py-4 rounded-lg font-semibold text-lg hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Contact Options */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-semibold mb-6">Ways to Connect</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
                  <SpeakerWaveIcon className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Speaking Engagements</h3>
                    <p className="text-gray-700 mb-3">
                      Available for workshops, conferences, and educational events. 
                      I speak to organizations, schools, and communities ready to engage 
                      in meaningful conversations about systemic racism.
                    </p>
                    <p className="text-sm text-gray-600">
                      Topics include: Understanding systemic racism, having difficult conversations, 
                      and moving from awareness to action.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-gray-50 rounded-lg">
                  <UserGroupIcon className="w-8 h-8 text-primary-600 flex-shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Book Club Support</h3>
                    <p className="text-gray-700 mb-3">
                      Discussion guides, virtual Q&A sessions, and resources to help 
                      your book club navigate these important conversations with care and depth.
                    </p>
                    <p className="text-sm text-gray-600">
                      Perfect for workplace book clubs, community groups, and educational settings.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-primary-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-3">Connect on Social Media</h3>
              <div className="flex gap-4">
                <a 
                  href="https://linkedin.com/in/synergyunlimited" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-primary-600 text-white px-4 py-2 rounded font-medium hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
                >
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    </>
  )
}